import { createTheme, alpha, type Theme, type PaletteMode } from '@mui/material/styles';
import { getComponents } from './components';
import { buildPalette, colorPresets, defaultThemePreset } from './palette';
import { getShadows } from './shadows';
import { shape } from './shape';
import { spacing } from './spacing';
import { typography } from './typography';
import type { ThemePreset } from '@/types';

export { colorPresets, defaultThemePreset } from './palette';
export { typography } from './typography';
export { spacing } from './spacing';
export { shape } from './shape';

export const appLayout = {
  sidebarWidth: 280,
  sidebarMini: 92,
  topbarHeight: 80,
  contentMaxWidth: 1480,
  contentTopGap: 2,
  sectionGap: 4,
  panelGap: 3,
  nestedGap: 2.5,
} as const;

function buildCustomTokens(
  mode: PaletteMode,
  preset: ThemePreset,
  dynamicColors?: { primary: string; secondary: string; accent: string },
) {
  const isDark = mode === 'dark';
  const presetColors = dynamicColors
    ? { primary: { main: dynamicColors.primary }, secondary: { main: dynamicColors.secondary }, accent: { main: dynamicColors.accent } }
    : colorPresets[preset];

  return {
    gradients: {
      primary: `linear-gradient(135deg, ${presetColors.primary.main} 0%, ${presetColors.secondary.main} 100%)`,
      secondary: `linear-gradient(135deg, ${presetColors.secondary.main} 0%, ${presetColors.accent.main} 100%)`,
      accent: `linear-gradient(135deg, ${presetColors.accent.main} 0%, ${presetColors.primary.main} 100%)`,
      hero: `linear-gradient(135deg, ${alpha(presetColors.primary.main, isDark ? 0.9 : 0.95)} 0%, ${alpha(presetColors.secondary.main, isDark ? 0.85 : 0.9)} 55%, ${alpha(presetColors.accent.main, isDark ? 0.75 : 0.85)} 100%)`,
    },
    glass: {
      background: isDark ? 'rgba(15, 23, 42, 0.64)' : 'rgba(255, 255, 255, 0.58)',
      border: isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(255, 255, 255, 0.58)',
      shadow: isDark ? '0px 20px 48px rgba(2, 6, 23, 0.48)' : '0px 20px 48px rgba(15, 23, 42, 0.12)',
      blur: 'blur(18px)',
    },
    layout: appLayout,
  };
}

export function getAppTheme(
  mode: PaletteMode,
  preset: ThemePreset = defaultThemePreset,
  dynamicColors?: { primary: string; secondary: string; accent: string },
): Theme {
  const baseTheme = createTheme({
    palette: buildPalette(mode, preset, dynamicColors),
    typography,
    shape,
    spacing,
    shadows: getShadows(mode),
    custom: buildCustomTokens(mode, preset, dynamicColors),
  });

  return createTheme(baseTheme, {
    components: getComponents(baseTheme),
  });
}

export function getGlassStyle(theme: Theme) {
  return {
    background: theme.custom.glass.background,
    backdropFilter: theme.custom.glass.blur,
    border: `1px solid ${theme.custom.glass.border}`,
    boxShadow: theme.custom.glass.shadow,
  };
}

export const theme = getAppTheme('light', defaultThemePreset);

